import React, { useState, useEffect } from 'react';
import Card from "components/card";
import { MdSearch, MdEdit, MdDelete, MdError, MdFilterList, MdCalendarToday, MdScale, MdCategory, MdClose } from "react-icons/md";
import axios from 'axios';
import { toast, Toaster } from 'react-hot-toast';

const Loader = () => (
  <div className="flex h-[400px] items-center justify-center">
    <div className="relative">
      <div className="h-24 w-24 rounded-full border-t-4 border-b-4 border-brand-500"></div>
      <div className="absolute top-0 left-0 h-24 w-24 animate-spin rounded-full border-t-4 border-b-4 border-gray-200"></div>
      <div className="absolute top-1/2 left-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-500"></div>
      <div className="mt-8 text-center">
        <p className="text-lg font-medium text-gray-600 dark:text-white">Loading</p>
        <p className="text-sm text-gray-400">Please wait...</p>
      </div>
    </div>
  </div>
);

const ConsumptionTable = () => {
  const [consumptionData, setConsumptionData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    size: '',
    origin: ''
  });
  const itemsPerPage = 10;
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);

  // Fetch data from API
  useEffect(() => {
    const fetchConsumptionData = async () => {
      try {
        setLoading(true);
        const response = await axios.get('https://usibras-web-backend.onrender.com/api/fetch-consumption');
        if (response.data.status === 'success') {
          setConsumptionData(response.data.data);
        } else {
          setError('Failed to fetch data');
        }
      } catch (err) {
        setError('Error connecting to the server');
        console.error('Error fetching consumption data:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchConsumptionData();
  }, []);

  // Calculate summary statistics
  const calculateSummary = (data) => {
    const totalConsumptions = data.length;
    const totalWeight = data.reduce((sum, item) => sum + (parseFloat(item.quantity_consumed) || 0), 0);
    const totalBags = data.reduce((sum, item) => sum + (parseInt(item.bags_consumed) || 0), 0);
    const avgOutturn = data.reduce((sum, item) => sum + (parseFloat(item.outturn) || 0), 0) / (data.length || 1);

    // Format the total weight with commas and 2 decimal places
    const formattedWeight = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(totalWeight);

    return {
      totalConsumptions,
      totalWeight: formattedWeight,
      totalBags,
      avgOutturn: avgOutturn.toFixed(2)
    };
  };

  // Get unique values for filters
  const uniqueSizes = [...new Set(consumptionData.map(item => item.size))];
  const uniqueOrigins = [...new Set(consumptionData.map(item => item.origin))];

  // Apply filters
  const filteredData = consumptionData.filter(item => {
    const matchesSearch = 
      (item.origin && item.origin.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.size && item.size.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.stacking_zone && item.stacking_zone.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesDate = (!filters.startDate || new Date(item.date) >= new Date(filters.startDate)) &&
                       (!filters.endDate || new Date(item.date) <= new Date(filters.endDate));
    
    const matchesSize = !filters.size || item.size === filters.size;
    const matchesOrigin = !filters.origin || item.origin === filters.origin;

    return matchesSearch && matchesDate && matchesSize && matchesOrigin;
  });

  const summary = calculateSummary(filteredData);

  // Pagination
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleEdit = (id) => {
    // Implement edit functionality
    console.log(`Edit item with ID: ${id}`);
  };

  const handleDelete = (id) => {
    setRecordToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    setIsDeleting(true);
    try {
      const response = await axios.delete(`https://usibras-web-backend.onrender.com/delete_consumption_record/${recordToDelete}`);
      if (response.status === 200) {
        toast.success("Record deleted successfully");
        setConsumptionData(consumptionData.filter(item => item.id !== recordToDelete));
      }
    } catch (err) {
      console.error('Delete Error:', err);
      toast.error('Error deleting record. Please try again.');
    } finally {
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
      setRecordToDelete(null);
    }
  };

  const TABLE_HEAD = ["ID", "Date", "Origin", "Size", "Quantity", "Bags", "Stacking Zone", "Outturn", "Moisture", "Purity", "TDR", "Action"];

  return (
    <div className="mt-5 h-full">
      <Toaster position="top-right" />
      <Card extra={"w-full h-full p-4"}>
        {/* Summary Cards */}
        <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <Card extra="p-4">
            <div className="flex items-center gap-4">
              <div className="rounded-full bg-blue-50 p-3 dark:bg-navy-700">
                <MdCategory className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Consumptions</p>
                <p className="text-xl font-bold text-navy-700 dark:text-white">{summary.totalConsumptions}</p>
              </div>
            </div>
          </Card>

          <Card extra="p-4">
            <div className="flex items-center gap-4">
              <div className="rounded-full bg-green-50 p-3 dark:bg-navy-700">
                <MdScale className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Weight</p>
                <p className="text-xl font-bold text-navy-700 dark:text-white">{summary.totalWeight} kg</p>
              </div>
            </div>
          </Card>

          <Card extra="p-4">
            <div className="flex items-center gap-4">
              <div className="rounded-full bg-purple-50 p-3 dark:bg-navy-700">
                <MdCategory className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Bags</p>
                <p className="text-xl font-bold text-navy-700 dark:text-white">{summary.totalBags}</p>
              </div>
            </div>
          </Card>

          <Card extra="p-4">
            <div className="flex items-center gap-4">
              <div className="rounded-full bg-orange-50 p-3 dark:bg-navy-700">
                <MdScale className="h-6 w-6 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Average Outturn</p>
                <p className="text-xl font-bold text-navy-700 dark:text-white">{summary.avgOutturn}%</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Filters Section */}
        <div className="mb-8 rounded-xl border border-gray-200 bg-white p-4 dark:border-navy-700 dark:bg-navy-800">
          <div className="mb-4 flex items-center gap-2">
            <MdFilterList className="h-5 w-5 text-gray-500" />
            <h3 className="text-lg font-semibold text-gray-700 dark:text-white">Filters</h3>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="lg:col-span-2">
              <label className="mb-2 block text-sm font-medium text-gray-600 dark:text-gray-400">Date Range</label>
              <div className="flex gap-2">
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => setFilters({...filters, startDate: e.target.value})}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700"
                />
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => setFilters({...filters, endDate: e.target.value})}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700"
                />
              </div>
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-600 dark:text-gray-400">Size</label>
              <select
                value={filters.size}
                onChange={(e) => setFilters({...filters, size: e.target.value})}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700"
              >
                <option value="">All Sizes</option>
                {uniqueSizes.map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-600 dark:text-gray-400">Origin</label>
              <select
                value={filters.origin}
                onChange={(e) => setFilters({...filters, origin: e.target.value})}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700"
              >
                <option value="">All Origins</option>
                {uniqueOrigins.map(origin => (
                  <option key={origin} value={origin}>{origin}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button
              onClick={() => setFilters({startDate: '', endDate: '', size: '', origin: ''})}
              className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* Existing table header and search */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h4 className="text-xl font-bold text-navy-700 dark:text-white">
              Warehouse Consumption Records
            </h4>
            <p className="mt-1 text-sm text-gray-600">
              Track all raw cashew nut consumption from warehouse
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex h-10 items-center rounded-full bg-white px-4 dark:bg-navy-900">
              <span className="text-xl">
                <MdSearch className="h-5 w-5 text-gray-400" />
              </span>
              <input
                type="text"
                placeholder="Search..."
                className="ml-2 w-full bg-transparent text-sm font-medium text-gray-600 outline-none dark:text-white"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        {loading ? (
          <Loader />
        ) : error ? (
          <div className="flex h-[400px] items-center justify-center">
            <div className="text-center">
              <div className="mb-4 text-5xl text-red-500">
                <MdError className="mx-auto h-16 w-16" />
              </div>
              <p className="text-xl font-semibold text-gray-700 dark:text-white">
                Oops! Something went wrong
              </p>
              <p className="mt-2 text-gray-500 dark:text-gray-400">{error}</p>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  {TABLE_HEAD.map((head, index) => (
                    <th
                      key={index}
                      className="border-b border-gray-200 pb-[10px] pr-4 text-start dark:!border-navy-700"
                    >
                      <p className="text-sm font-bold tracking-wide text-gray-800 dark:text-white">
                        {head}
                      </p>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((item) => (
                  <tr key={item.id}>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                      <p className="text-sm font-bold text-navy-700 dark:text-white">
                        {item.id}
                      </p>
                    </td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                      <p className="text-sm font-bold text-navy-700 dark:text-white">
                        {item.date ? new Date(item.date).toLocaleDateString() : 'N/A'}
                      </p>
                    </td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                      <p className="text-sm font-bold text-navy-700 dark:text-white">
                        {item.origin}
                      </p>
                    </td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                      <p className="text-sm font-bold text-navy-700 dark:text-white">
                        {item.size}
                      </p>
                    </td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                      <p className="text-sm font-bold text-navy-700 dark:text-white">
                        {item.quantity_consumed} kg
                      </p>
                    </td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                      <p className="text-sm font-bold text-navy-700 dark:text-white">
                        {item.bags_consumed}
                      </p>
                    </td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                      <p className="text-sm font-bold text-navy-700 dark:text-white">
                        {item.stacking_zone}
                      </p>
                    </td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                      <p className="text-sm font-bold text-navy-700 dark:text-white">
                        {item.outturn}%
                      </p>
                    </td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                      <p className="text-sm font-bold text-navy-700 dark:text-white">
                        {item.moisture_level}%
                      </p>
                    </td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                      <p className="text-sm font-bold text-navy-700 dark:text-white">
                        {item.purity ? `${item.purity}%` : 'N/A'}
                      </p>
                    </td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                      <p className="text-sm font-bold text-navy-700 dark:text-white">
                        {item.defective_rate ? `${item.defective_rate}%` : 'N/A'}
                      </p>
                    </td>
                    <td className="pt-[14px] pb-[18px] sm:text-[14px]">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="rounded-lg bg-red-50 p-2 text-red-600 hover:bg-red-100 dark:bg-navy-700 dark:text-red-400 dark:hover:bg-navy-600"
                        >
                          <MdDelete className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-6 flex items-center justify-between">
          <p className="text-sm font-medium text-navy-700 dark:text-white">
            Showing {paginatedData.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0} to {Math.min(currentPage * itemsPerPage, filteredData.length)} of {filteredData.length} entries
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className={`linear rounded-md px-3 py-1 text-sm font-medium transition duration-200 ${
                currentPage === 1
                  ? "cursor-not-allowed bg-gray-300 text-gray-500"
                  : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
              }`}
            >
              First
            </button>
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              className={`linear rounded-md px-3 py-1 text-sm font-medium transition duration-200 ${
                currentPage === 1
                  ? "cursor-not-allowed bg-gray-300 text-gray-500"
                  : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
              }`}
            >
              Previous
            </button>
            
            {/* Page number buttons */}
            {[...Array(Math.min(5, totalPages))].map((_, index) => {
              // Calculate which page numbers to show
              let pageNum;
              if (totalPages <= 5) {
                pageNum = index + 1;
              } else if (currentPage <= 3) {
                pageNum = index + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + index;
              } else {
                pageNum = currentPage - 2 + index;
              }
              
              return (
                <button
                  key={index}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`linear rounded-md px-3 py-1 text-sm font-medium transition duration-200 ${
                    currentPage === pageNum
                      ? "bg-brand-700 text-white"
                      : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages || totalPages === 0}
              className={`linear rounded-md px-3 py-1 text-sm font-medium transition duration-200 ${
                currentPage === totalPages || totalPages === 0
                  ? "cursor-not-allowed bg-gray-300 text-gray-500"
                  : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
              }`}
            >
              Next
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages || totalPages === 0}
              className={`linear rounded-md px-3 py-1 text-sm font-medium transition duration-200 ${
                currentPage === totalPages || totalPages === 0
                  ? "cursor-not-allowed bg-gray-300 text-gray-500"
                  : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
              }`}
            >
              Last
            </button>
          </div>
        </div>

        {/* Add DeleteConfirmationModal */}
        <DeleteConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setRecordToDelete(null);
          }}
          onConfirm={confirmDelete}
          recordId={recordToDelete}
          isDeleting={isDeleting}
        />
      </Card>
    </div>
  );
};

// Add DeleteConfirmationModal component
const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm, recordId, isDeleting }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative w-full max-w-md rounded-xl bg-white p-6 shadow-2xl dark:bg-navy-800">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-xl font-bold text-navy-700 dark:text-white">Confirm Deletion</h3>
          <button
            onClick={onClose}
            disabled={isDeleting}
            className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-navy-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <MdClose className="h-6 w-6" />
          </button>
        </div>
        <p className="mb-6 text-gray-600 dark:text-gray-300">
          Are you sure you want to delete this record? This action cannot be undone.
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isDeleting}
            className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 dark:border-navy-600 dark:text-gray-300 dark:hover:bg-navy-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isDeleting}
            className="flex items-center gap-2 rounded-lg bg-red-500 px-4 py-2 text-sm font-medium text-white hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isDeleting ? (
              <>
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                Deleting...
              </>
            ) : (
              'Delete'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConsumptionTable;
